// Vipps state management utility module
// Handles sessionStorage and localStorage operations for Vipps authentication state, validation, and duplicate detection
// Uses dual storage strategy: sessionStorage as primary, localStorage as fallback for better mobile browser compatibility

const VIPPS_CODE_PREFIX = 'vipps_code_';
const VIPPS_STATE_PREFIX = 'vipps_state_';
const CURRENT_AUTH_TOKEN_KEY = 'current_auth_token';
const STALE_AGE_MINUTES = 10;
const CALLBACK_STALE_AGE_MINUTES = 5;
const STATE_STALE_AGE_MINUTES = 10;
const AUTH_TOKEN_TTL_MS = 10 * 60 * 1000; // 10 minutes for auth token

// Storage availability detection
function isSessionStorageAvailable() {
    try {
        var test = '__sessionStorage_test__';
        window.sessionStorage.setItem(test, test);
        window.sessionStorage.removeItem(test);
        return true;
    } catch (_) {
        return false;
    }
}

function isLocalStorageAvailable() {
    try {
        var test = '__localStorage_test__';
        window.localStorage.setItem(test, test);
        window.localStorage.removeItem(test);
        return true;
    } catch (_) {
        return false;
    }
}

function getSessionStorageItem(key) {
    try {
        return window.sessionStorage.getItem(key);
    } catch (_) {
        return null;
    }
}

function getLocalStorageItem(key) {
    try {
        return window.localStorage.getItem(key);
    } catch (_) {
        return null;
    }
}

function setSessionStorageItem(key, value) {
    try {
        window.sessionStorage.setItem(key, value);
        return true;
    } catch (error) {
        console.warn('Failed to set sessionStorage item:', key, error);
        return false;
    }
}

function setLocalStorageItem(key, value) {
    try {
        window.localStorage.setItem(key, value);
        return true;
    } catch (error) {
        console.warn('Failed to set localStorage item:', key, error);
        return false;
    }
}

// Helper to check if data is expired (for TTL-enabled keys)
function isDataExpiredForKey(key, createdAt) {
    if (!createdAt) return false;
    if (key === CURRENT_AUTH_TOKEN_KEY) {
        const now = Date.now();
        const age = now - createdAt;
        return age > AUTH_TOKEN_TTL_MS;
    }
    return false;
}

// Dual storage: write to both sessionStorage and localStorage
// Includes TTL support for current_auth_token
function setDualStorageItem(key, value) {
    var sessionSuccess = false;
    var localSuccess = false;
    
    // Check if this key should have TTL
    var valueToStore = value;
    if (key === CURRENT_AUTH_TOKEN_KEY) {
        // Wrap auth token with timestamp for TTL validation
        valueToStore = JSON.stringify({
            value: value,
            createdAt: Date.now()
        });
    }
    
    if (isSessionStorageAvailable()) {
        sessionSuccess = setSessionStorageItem(key, valueToStore);
    }
    
    if (isLocalStorageAvailable()) {
        localSuccess = setLocalStorageItem(key, valueToStore);
    }
    
    // Return true if at least one storage succeeded
    return sessionSuccess || localSuccess;
}

// Dual storage: read from sessionStorage first, then localStorage as fallback
// Includes TTL validation for current_auth_token
function getDualStorageItem(key) {
    // Try sessionStorage first
    var value = getSessionStorageItem(key);
    if (value !== null) {
        // Check TTL for current_auth_token
        if (key === CURRENT_AUTH_TOKEN_KEY) {
            try {
                var parsed = JSON.parse(value);
                if (parsed && typeof parsed === 'object' && 'value' in parsed && 'createdAt' in parsed) {
                    if (isDataExpiredForKey(key, parsed.createdAt)) {
                        console.warn('Auth token expired - age:', Math.round((Date.now() - parsed.createdAt) / 1000 / 60), 'minutes');
                        removeDualStorageItem(key);
                        return null;
                    }
                    return parsed.value;
                }
            } catch (_) {
                // Not TTL-enabled format, return as-is (backwards compatibility)
            }
        }
        return value;
    }
    
    // Fallback to localStorage
    value = getLocalStorageItem(key);
    if (value !== null) {
        // Check TTL for current_auth_token
        if (key === CURRENT_AUTH_TOKEN_KEY) {
            try {
                var parsed = JSON.parse(value);
                if (parsed && typeof parsed === 'object' && 'value' in parsed && 'createdAt' in parsed) {
                    if (isDataExpiredForKey(key, parsed.createdAt)) {
                        console.warn('Auth token expired - age:', Math.round((Date.now() - parsed.createdAt) / 1000 / 60), 'minutes');
                        removeDualStorageItem(key);
                        return null;
                    }
                    // Sync back to sessionStorage if valid
                    if (isSessionStorageAvailable()) {
                        try {
                            setSessionStorageItem(key, value);
                        } catch (_) {
                            // Ignore sync errors
                        }
                    }
                    return parsed.value;
                }
            } catch (_) {
                // Not TTL-enabled format, return as-is (backwards compatibility)
            }
        }
        
        // If found in localStorage but not sessionStorage, sync it back to sessionStorage
        // This helps in cross-browser scenarios
        if (isSessionStorageAvailable()) {
            try {
                setSessionStorageItem(key, value);
            } catch (_) {
                // Ignore sync errors
            }
        }
        return value;
    }
    
    return null;
}

function removeSessionStorageItem(key) {
    try {
        window.sessionStorage.removeItem(key);
    } catch (_) {
        // noop
    }
}

function removeLocalStorageItem(key) {
    try {
        window.localStorage.removeItem(key);
    } catch (_) {
        // noop
    }
}

// Dual storage: remove from both storages
function removeDualStorageItem(key) {
    removeSessionStorageItem(key);
    removeLocalStorageItem(key);
}

function cleanupStaleSessionStorage() {
    try {
        var now = Date.now();
        var keysToRemove = [];
        
        // Clean up sessionStorage
        if (isSessionStorageAvailable()) {
            for (var i = 0; i < window.sessionStorage.length; i++) {
                var key = window.sessionStorage.key(i);
                if (key && (key.startsWith(VIPPS_CODE_PREFIX) || key.startsWith(VIPPS_STATE_PREFIX))) {
                    var value = getSessionStorageItem(key);
                    if (value) {
                        try {
                            var timestamp = parseInt(value, 10);
                            if (!isNaN(timestamp)) {
                                var ageMinutes = (now - timestamp) / (1000 * 60);
                                if (ageMinutes > STALE_AGE_MINUTES) {
                                    keysToRemove.push(key);
                                }
                            } else {
                                // For state objects, check createdAt
                                var stateData = JSON.parse(value);
                                if (stateData && stateData.createdAt) {
                                    var ageMinutes = (now - stateData.createdAt) / (1000 * 60);
                                    if (ageMinutes > STALE_AGE_MINUTES) {
                                        keysToRemove.push(key);
                                    }
                                }
                            }
                        } catch (_) {
                            // If parsing fails, remove the entry
                            keysToRemove.push(key);
                        }
                    }
                }
            }
        }
        
        // Clean up localStorage
        if (isLocalStorageAvailable()) {
            for (var i = 0; i < window.localStorage.length; i++) {
                var key = window.localStorage.key(i);
                if (key && (key.startsWith(VIPPS_CODE_PREFIX) || key.startsWith(VIPPS_STATE_PREFIX))) {
                    var value = getLocalStorageItem(key);
                    if (value) {
                        try {
                            var timestamp = parseInt(value, 10);
                            if (!isNaN(timestamp)) {
                                var ageMinutes = (now - timestamp) / (1000 * 60);
                                if (ageMinutes > STALE_AGE_MINUTES) {
                                    keysToRemove.push(key);
                                }
                            } else {
                                // For state objects, check createdAt
                                var stateData = JSON.parse(value);
                                if (stateData && stateData.createdAt) {
                                    var ageMinutes = (now - stateData.createdAt) / (1000 * 60);
                                    if (ageMinutes > STALE_AGE_MINUTES) {
                                        keysToRemove.push(key);
                                    }
                                }
                            }
                        } catch (_) {
                            // If parsing fails, remove the entry
                            keysToRemove.push(key);
                        }
                    }
                }
            }
        }
        
        // Remove stale entries from both storages
        keysToRemove.forEach(function(key) {
            removeDualStorageItem(key);
        });
    } catch (error) {
        console.warn('Error cleaning up stale storage:', error);
    }
}

function hashCode(code) {
    if (!code) return '';
    // Use first 32 characters as hash for storage key
    return code.substring(0, 32);
}

function generateNonce() {
    try {
        // Try to use crypto.randomUUID if available (best option)
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID().replace(/-/g, '');
        }
    } catch (_) {
        // Continue to next fallback
    }
    
    try {
        // Fallback: Use crypto.getRandomValues for better entropy than Math.random
        if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
            var array = new Uint8Array(16);
            crypto.getRandomValues(array);
            // Convert to hex string
            var hex = Array.from(array, function(byte) {
                return ('0' + (byte & 0xFF).toString(16)).slice(-2);
            }).join('');
            return hex;
        }
    } catch (_) {
        // Continue to final fallback
    }
    
    // Final fallback: Use Math.random with timestamp for additional entropy
    // Note: This is less secure but ensures we always return a value
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15) + 
           Date.now().toString(36) + 
           Math.random().toString(36).substring(2, 15);
}

function generateAuthToken() {
    var timestamp = Date.now();
    var randomString = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    return timestamp + '-' + randomString;
}

function parseState(stateString) {
    try {
        if (!stateString) {
            return null;
        }
        
        // State is Base64-encoded for URL-safe transport (format: login/{deviceUuid}|{timestamp}|{nonce})
        // First decode URL encoding, then decode Base64
        var urlDecoded = decodeURIComponent(stateString);
        
        // Decode Base64 (URL-safe: - becomes +, _ becomes /, add padding if needed)
        var base64State = urlDecoded
            .replace(/-/g, '+')  // URL-safe base64: - becomes +
            .replace(/_/g, '/');  // URL-safe base64: _ becomes /
        
        // Add padding if needed (Base64 strings should be multiple of 4)
        var padding = 4 - (base64State.length % 4);
        if (padding !== 4) {
            base64State += '='.repeat(padding);
        }
        
        // Decode Base64 to get original state string
        var decoded;
        try {
            // Use atob for Base64 decoding (browser API)
            decoded = atob(base64State);
        } catch (base64Error) {
            console.warn('Error decoding Base64 state parameter:', base64Error);
            return null;
        }
        
        // Format: login/{deviceUuid}|{timestamp}|{nonce}
        if (!decoded.startsWith('login/')) {
            return null;
        }
        
        var parts = decoded.substring(6).split('|'); // Remove 'login/' prefix
        if (parts.length !== 3) {
            return null;
        }
        
        return {
            deviceUuid: parts[0],
            timestamp: parseInt(parts[1], 10),
            nonce: parts[2]
        };
    } catch (error) {
        console.warn('Error parsing state parameter:', error);
        return null;
    }
}

function generateState(deviceUuid, timestamp, nonce) {
    return 'login/' + deviceUuid + '|' + timestamp + '|' + nonce;
}

function storeState(deviceUuid, stateData) {
    try {
        var key = VIPPS_STATE_PREFIX + deviceUuid;
        var value = JSON.stringify(stateData);
        // Use dual storage: write to both sessionStorage and localStorage
        var success = setDualStorageItem(key, value);
        if (!success) {
            console.warn('Failed to store state in both storages. Device:', deviceUuid);
        }
    } catch (error) {
        console.warn('Error storing state:', error);
    }
}

function getStoredState(deviceUuid) {
    try {
        var key = VIPPS_STATE_PREFIX + deviceUuid;
        // Use dual storage: read from sessionStorage first, then localStorage
        var value = getDualStorageItem(key);
        if (value) {
            return JSON.parse(value);
        }
        return null;
    } catch (error) {
        console.warn('Error retrieving stored state:', error);
        return null;
    }
}

function isCodeProcessed(code) {
    try {
        if (!code) return false;
        var codeHash = hashCode(code);
        var key = VIPPS_CODE_PREFIX + codeHash;
        // Use dual storage: check both sessionStorage and localStorage
        var value = getDualStorageItem(key);
        return value !== null;
    } catch (error) {
        console.warn('Error checking if code is processed:', error);
        return false;
    }
}

function markCodeAsProcessed(code) {
    try {
        if (!code) return;
        var codeHash = hashCode(code);
        var key = VIPPS_CODE_PREFIX + codeHash;
        var timestamp = Date.now();
        // Use dual storage: write to both sessionStorage and localStorage
        setDualStorageItem(key, timestamp.toString());
    } catch (error) {
        console.warn('Error marking code as processed:', error);
    }
}

function isStateValid(stateData, deviceUuid, maxAgeMinutes) {
    try {
        if (!stateData || !deviceUuid) {
            return false;
        }
        
        // Use configurable max age, default to STATE_STALE_AGE_MINUTES
        var maxAge = maxAgeMinutes !== undefined ? maxAgeMinutes : STATE_STALE_AGE_MINUTES;
        
        var storedState = getStoredState(deviceUuid);
        if (!storedState) {
            return false;
        }
        
        // Check if state matches
        if (storedState.state !== stateData.state) {
            return false;
        }
        
        // Check if nonce matches
        if (storedState.nonce !== stateData.nonce) {
            return false;
        }
        
        // Enhanced timestamp validation with configurable max age
        if (!storedState.createdAt) {
            console.warn('Stored state missing createdAt timestamp');
            return false;
        }
        
        var now = Date.now();
        var ageMinutes = (now - storedState.createdAt) / (1000 * 60);
        
        if (ageMinutes > maxAge) {
            console.warn('State expired. Age:', ageMinutes.toFixed(2), 'minutes, Max:', maxAge, 'minutes');
            return false;
        }
        
        // Additional validation: ensure createdAt is not in the future (clock skew protection)
        if (storedState.createdAt > now) {
            console.warn('State createdAt is in the future, possible clock skew');
            return false;
        }
        
        return true;
    } catch (error) {
        console.warn('Error validating state:', error);
        return false;
    }
}

function isCallbackStale(stateTimestamp, maxAgeMinutes) {
    try {
        if (!stateTimestamp) {
            return true;
        }
        
        var now = Date.now();
        // Convert timestamp to milliseconds if it's in seconds
        var timestampMs = stateTimestamp;
        if (stateTimestamp < 10000000000) {
            // Likely in seconds, convert to milliseconds
            timestampMs = stateTimestamp * 1000;
        }
        
        var ageMinutes = (now - timestampMs) / (1000 * 60);
        return ageMinutes > (maxAgeMinutes || CALLBACK_STALE_AGE_MINUTES);
    } catch (error) {
        console.warn('Error checking if callback is stale:', error);
        return true; // Treat as stale on error
    }
}

function storeAuthToken(token) {
    try {
        // Use dual storage: write to both sessionStorage and localStorage
        setDualStorageItem(CURRENT_AUTH_TOKEN_KEY, token);
    } catch (error) {
        console.warn('Error storing auth token:', error);
    }
}

function getAuthToken() {
    try {
        // Use dual storage: read from sessionStorage first, then localStorage
        return getDualStorageItem(CURRENT_AUTH_TOKEN_KEY);
    } catch (error) {
        console.warn('Error retrieving auth token:', error);
        return null;
    }
}

function clearVippsState(deviceUuid, code) {
    try {
        // Clear state from both storages
        if (deviceUuid) {
            var stateKey = VIPPS_STATE_PREFIX + deviceUuid;
            removeDualStorageItem(stateKey);
        }
        
        // Clear code from both storages
        if (code) {
            var codeHash = hashCode(code);
            var codeKey = VIPPS_CODE_PREFIX + codeHash;
            removeDualStorageItem(codeKey);
        }
        
        // Clear auth token from both storages
        removeDualStorageItem(CURRENT_AUTH_TOKEN_KEY);
    } catch (error) {
        console.warn('Error clearing Vipps state:', error);
    }
}

function extractStateFromLoginURL(loginURL) {
    try {
        if (!loginURL) {
            return null;
        }
        
        var url = new URL(loginURL);
        var stateParam = url.searchParams.get('state');
        if (stateParam) {
            return decodeURIComponent(stateParam);
        }
        return null;
    } catch (error) {
        console.warn('Error extracting state from login URL:', error);
        return null;
    }
}

export const vippsStateManager = {
    cleanupStaleSessionStorage,
    hashCode,
    generateNonce,
    generateAuthToken,
    parseState,
    generateState,
    storeState,
    getStoredState,
    isCodeProcessed,
    markCodeAsProcessed,
    isStateValid,
    isCallbackStale,
    storeAuthToken,
    getAuthToken,
    clearVippsState,
    extractStateFromLoginURL
};

